import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import type { HttpInterceptorFn } from '@angular/common/http';
import { of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { GrupoProducto, GrupoVendedor } from '../admin/compras/compras.model';
import * as I from './demo-interno';
import * as V from './demo-vendedor';

/** Las pantallas de la guía a fondo viven en /vendedor/tutorial/... y /admin/tutorial/... */
export const enGuia = () => typeof location !== 'undefined' && location.pathname.includes('/tutorial/');

const paginado = <T>(data: T[]) => ({ success: true, data, meta: { page: 1, limit: data.length || 20, total: data.length, totalPages: 1 } });
const ok = <T>(data: T) => ({ success: true, data });

// La vista "por producto" de compras, armada desde las mismas prendas del ejemplo
function porProducto(): GrupoProducto[] {
  const mapa = new Map<string, GrupoProducto>();
  for (const g of I.TABLERO.grupos as GrupoVendedor[]) {
    for (const c of g.clientes) {
      for (const u of c.unidades) {
        const clave = `${u.producto.titulo}|${u.talla}`;
        const previo = mapa.get(clave);
        if (previo) {
          previo.cantidad += 1;
          previo.unidades.push(u);
        } else {
          mapa.set(clave, { variante_id: u.variante_id, talla: u.talla, opciones: u.opciones, producto: u.producto, cantidad: 1, base_cop_cotizado: u.base_cop_cotizado, unidades: [u] });
        }
      }
    }
  }
  return [...mapa.values()];
}

// GET de la guía → datos de ejemplo. Lo que no está aquí sigue al servidor (catálogo, avisos)
const RESPUESTAS: [RegExp, (url: URL) => unknown][] = [
  // Vendedor
  [/^\/vendedor\/resumen$/, () => ok(V.RESUMEN)],
  [/^\/vendedor\/clientes$/, () => paginado(V.CLIENTES)],
  [/^\/vendedor\/pedidos-cliente$/, (u) => paginado(u.searchParams.get('estado') ? V.PEDIDOS.filter((p) => p.estado === u.searchParams.get('estado')) : V.PEDIDOS)],
  [/^\/vendedor\/ciclos\/actual$/, () => ok(V.PANEL_CICLO)],
  [/^\/vendedor\/pedidos-ciclo$/, () => ok(V.PEDIDOS_CICLO)],
  [/^\/vendedor\/pedidos-ciclo\/[\w-]+$/, () => ok(V.PEDIDO_CICLO)],
  [/^\/vendedor\/estado-cuenta$/, () => ok(V.ESTADO_CUENTA)],
  [/^\/vendedor\/red$/, () => ok(V.RED)],
  [/^\/vendedor\/cuenta-bancaria$/, () => ok(null)],
  [/^\/vendedor\/retiros$/, () => ok([])],
  [/^\/vendedor\/puntos$/, () => ok(V.PUNTOS)],
  [/^\/vendedor\/premios$/, () => ok(V.PREMIOS)],
  [/^\/vendedor\/premios\/ideas$/, () => ok(V.IDEAS)],
  [/^\/vendedor\/canjes$/, () => ok(V.CANJES)],
  // D-149: un cliente nuevo asignado y un carrito de su link, para mostrar cómo se ven
  [
    /^\/vendedor\/carritos-recibidos$/,
    () =>
      ok([
        { codigo: 'EJEMPLO2', origen_vendedor: 'ASIGNADO', cliente_nombre: 'Rosa Méndez', cliente_telefono: '+584145123478', estado_region: 'Trujillo', municipio: 'Miranda', parroquia: 'El Dividive', asignacion_vence_en: new Date(Date.now() + 95 * 60_000).toISOString(), created_at: new Date().toISOString(), unidades: 3, total: '58.50' },
        // D-157: carrito de su propio link, con los mensajes listos en «Escribirle»
        { codigo: 'EJEMPLO3', origen_vendedor: 'ENLACE', cliente_nombre: 'Luisa Pérez', cliente_telefono: '+584245551290', estado_region: null, municipio: null, parroquia: null, asignacion_vence_en: null, aceptado_en: null, created_at: new Date().toISOString(), unidades: 2, total: '31.00' },
      ]),
  ],
  // Equipo interno
  [/^\/admin\/abonos\/cola$/, () => paginado(I.COLA_ABONOS)],
  [/^\/admin\/ordenes-pago\/[\w-]+\/comprobante-url$/, () => ok({ url: 'tutorial/comprobante.svg', tipo: 'image/svg+xml' })],
  [/^\/admin\/ordenes-pago\/([\w-]+)$/, (u) => ok(I.COLA_ABONOS.find((o) => u.pathname.endsWith(o.id)) ?? I.COLA_ABONOS[0])],
  [/^\/admin\/compras\/ciclos$/, () => ok(I.CICLOS_COMPRAS)],
  [/^\/admin\/ciclos\/[\w-]+\/compras$/, (u) => ok(u.searchParams.get('vista') === 'producto' ? { ...I.TABLERO, vista: 'producto', grupos: porProducto() } : I.TABLERO)],
  [/^\/admin\/ordenes-shein$/, () => ok(I.ORDENES_SHEIN)],
  [/^\/admin\/ordenes-shein\/[\w-]+\/comprobante-url$/, () => ok({ url: 'tutorial/comprobante.svg', tipo: 'image/svg+xml' })],
  [/^\/admin\/bodega\/ordenes-shein$/, () => ok(I.BODEGA_ORDENES)],
  [/^\/admin\/bodega\/pedidos$/, () => ok(I.BODEGA_PEDIDOS)],
  [/^\/admin\/reclamos-shein$/, () => ok([])],
  [/^\/admin\/lotes$/, () => ok(I.LOTES)],
  [/^\/admin\/entregas\/buscar$/, () => ok(I.BUSQUEDA_ENTREGA)],
  [/^\/admin\/entregas\/pedidos\/[\w-]+\/resumen$/, () => ok(I.RESUMEN_ENTREGA)],
];

/**
 * Guía a fondo (D-141): en las pantallas /tutorial/ las mismas páginas reales muestran una
 * cuenta de ejemplo. Nada se guarda: lo que intente cambiar algo recibe un aviso amable y no
 * llega al servidor.
 */
export const demoInterceptor: HttpInterceptorFn = (req, next) => {
  if (!enGuia() || !req.url.startsWith(environment.apiUrl)) return next(req);
  const url = new URL(req.urlWithParams, location.origin);
  const ruta = url.pathname.slice(new URL(environment.apiUrl, location.origin).pathname.length);
  if (/^\/(auth|notificaciones|public)\b/.test(ruta)) return next(req);

  if (req.method !== 'GET') {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 409,
          url: req.url,
          error: { success: false, error: { code: 'MODO_EJEMPLO', message: 'Esto es un ejemplo de la guía: aquí no se guarda nada. Hazlo en la sección real cuando lo necesites.' } },
        }),
    );
  }
  const r = RESPUESTAS.find(([re]) => re.test(ruta));
  return r ? of(new HttpResponse({ status: 200, body: r[1](url) })) : next(req);
};
